import React, { useState, useEffect } from "react";
import { VendorService, UserService } from "src/services";
import {
  COMPLETION_STATUS_OPTIONS,
  CHECK_IN_STATUS_OPTIONS,
  CHECK_IN_FLAGGED_ISSUE_OPTIONS,
  CONFIRMATION_STATUS_OPTIONS,
  defaultMaxStartDateRangeDate,
  defaultStartDateRangeDate
} from "src/helpers";
import { PurchaseOrdersFilters, DateTimeRange } from "src/types";
import { CreationDateSortOptions, PurchaseOrderColumnHeader } from "src/enums";
import { LoadingState } from "src/components";
import { DropdownField } from "src/components/ui/inputs";
import { Button } from "src/shadcn/ui/button";
import { Input } from "src/shadcn/ui/input";
import { Badge } from "src/shadcn/ui/badge";
import { AlertCircle, Trash2 } from "lucide-react";

interface FilterPurchaseOrdersProps {
  onFilterChange: (filters: PurchaseOrdersFilters) => void;
  currentFilter?: PurchaseOrdersFilters;
  vendorService?: VendorService;
  userService?: UserService;
  dateTimeRange: DateTimeRange;
  onDateTimeRangeChange: (range: DateTimeRange) => void;
  deliveryDateTimeRange: DateTimeRange;
  onDeliveryDateTimeRangeChange: (range: DateTimeRange) => void;
  receiptDateTimeRange: DateTimeRange;
  onReceiptDateTimeRangeChange: (range: DateTimeRange) => void;
  sortOption: CreationDateSortOptions;
  onSortChange: (option: CreationDateSortOptions) => void;
  columnsToHide: PurchaseOrderColumnHeader[];
  onColumnsToHideChange: (columns: PurchaseOrderColumnHeader[]) => void;
  allColumns: PurchaseOrderColumnHeader[];
  onClose?: () => void;
  isOpen?: boolean;
}

export const FilterPurchaseOrders: React.FC<FilterPurchaseOrdersProps> = ({
  onFilterChange,
  currentFilter,
  vendorService,
  userService,
  dateTimeRange,
  onDateTimeRangeChange,
  deliveryDateTimeRange,
  onDeliveryDateTimeRangeChange,
  receiptDateTimeRange,
  onReceiptDateTimeRangeChange,
  sortOption,
  onSortChange,
  columnsToHide,
  onColumnsToHideChange,
  allColumns,
  onClose,
  isOpen
}) => {
  const [dropdownOptions, setDropdownOptions] = useState<{
    locations?: string[];
    vendorNames?: string[];
    senderEmails?: string[];
  }>({});
  const [loadingDropdownOptions, setLoadingDropdownOptions] = useState(false);
  // const [dropdownOptionsLoaded, setDropdownOptionsLoaded] = useState(false);
  const [dropdownOptionsFetchError, setDropdownOptionsFetchError] = useState(false);

  const [localDateTimeRange, setLocalDateTimeRange] = useState<DateTimeRange>(dateTimeRange);
  const [localDeliveryDateTimeRange, setLocalDeliveryDateTimeRange] = useState<DateTimeRange>(deliveryDateTimeRange);
  const [localReceiptDateTimeRange, setLocalReceiptDateTimeRange] = useState<DateTimeRange>(receiptDateTimeRange);
  const [localSortOption, setLocalSortOption] = useState<CreationDateSortOptions>(sortOption);

  const [filters, setFilters] = useState<Partial<PurchaseOrdersFilters>>({
    completionStatusFilter: currentFilter?.completionStatusFilter,
    checkInStatusFilter: currentFilter?.checkInStatusFilter,
    checkInFlaggedIssueFilter: currentFilter?.checkInFlaggedIssueFilter,
    confirmationStatusFilter: currentFilter?.confirmationStatusFilter,
    origin: currentFilter?.origin,
    vendorName: currentFilter?.vendorName,
    senderEmail: currentFilter?.senderEmail,
    hasTrackingNo: currentFilter?.hasTrackingNo ?? undefined,
    hasInvoice: currentFilter?.hasInvoice ?? undefined,
    isOverdueDelivery: currentFilter?.isOverdueDelivery ?? undefined,
    isInvoicePaymentDue: currentFilter?.isInvoicePaymentDue ?? undefined,
    poRelationshipType: currentFilter?.poRelationshipType
  });

  // Reset local state to match applied filters when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setLocalDateTimeRange(dateTimeRange);
      setLocalDeliveryDateTimeRange(deliveryDateTimeRange);
      setLocalReceiptDateTimeRange(receiptDateTimeRange);
      setLocalSortOption(sortOption);
      setFilters({
        completionStatusFilter: currentFilter?.completionStatusFilter,
        checkInStatusFilter: currentFilter?.checkInStatusFilter,
        checkInFlaggedIssueFilter: currentFilter?.checkInFlaggedIssueFilter,
        confirmationStatusFilter: currentFilter?.confirmationStatusFilter,
        origin: currentFilter?.origin,
        vendorName: currentFilter?.vendorName,
        senderEmail: currentFilter?.senderEmail,
        hasTrackingNo: currentFilter?.hasTrackingNo ?? undefined,
        hasInvoice: currentFilter?.hasInvoice ?? undefined,
        isOverdueDelivery: currentFilter?.isOverdueDelivery ?? undefined,
        isInvoicePaymentDue: currentFilter?.isInvoicePaymentDue ?? undefined,
        poRelationshipType: currentFilter?.poRelationshipType
      });
    }
  }, [isOpen, dateTimeRange, deliveryDateTimeRange, receiptDateTimeRange, sortOption, currentFilter]);

  useEffect(() => {
    if (isOpen) {
      fetchDropdownOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchDropdownOptions = async () => {
    if (!vendorService || !userService) {
      return;
    }
    try {
      setLoadingDropdownOptions(true);
      const [locations, vendorNames, senderEmails] = await Promise.all([
        vendorService.getVendorLocations(),
        vendorService.getVendorNames(),
        userService.getUserEmails(["HEALF_OPERATIONS", "HEALF_OPERATIONS_MANAGER", "ADMIN"])
      ]);

      setDropdownOptions({
        locations,
        vendorNames,
        senderEmails
      });
      // setDropdownOptionsLoaded(true);
    } catch (error) {
      setDropdownOptionsFetchError(true);
    } finally {
      setLoadingDropdownOptions(false);
    }
  };

  const handleFilterChange = <K extends keyof PurchaseOrdersFilters>(key: K, value: PurchaseOrdersFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    const updatedFilters: PurchaseOrdersFilters = {
      ...filters,
      hasTrackingNo: filters.hasTrackingNo ?? undefined,
      hasInvoice: filters.hasInvoice ?? undefined,
      isOverdueDelivery: filters.isOverdueDelivery ?? undefined,
      isInvoicePaymentDue: filters.isInvoicePaymentDue ?? undefined,
      filterPopoverVisible: false
    } as PurchaseOrdersFilters;

    // Apply date range and sort changes
    onDateTimeRangeChange(localDateTimeRange);
    onDeliveryDateTimeRangeChange(localDeliveryDateTimeRange);
    onReceiptDateTimeRangeChange(localReceiptDateTimeRange);
    onSortChange(localSortOption);
    onFilterChange(updatedFilters);

    // Close the sidebar
    onClose?.();
  };

  const handleClearAllFilters = () => {
    const clearedFilters: PurchaseOrdersFilters = {
      completionStatusFilter: undefined,
      checkInStatusFilter: undefined,
      checkInFlaggedIssueFilter: undefined,
      confirmationStatusFilter: undefined,
      origin: undefined,
      vendorName: undefined,
      senderEmail: undefined,
      hasTrackingNo: undefined,
      hasInvoice: undefined,
      isOverdueDelivery: undefined,
      isInvoicePaymentDue: undefined,
      poRelationshipType: undefined,
      filterPopoverVisible: false
    };
    setFilters(clearedFilters);

    // Reset date range and sort to defaults
    const defaultRange = { start: defaultStartDateRangeDate(), end: undefined };
    setLocalDateTimeRange(defaultRange);
    onDateTimeRangeChange(defaultRange);

    const emptyRange = { start: undefined, end: undefined };
    setLocalDeliveryDateTimeRange(emptyRange);
    onDeliveryDateTimeRangeChange(emptyRange);
    setLocalReceiptDateTimeRange(emptyRange);
    onReceiptDateTimeRangeChange(emptyRange);

    setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC);
    onSortChange(CreationDateSortOptions.CREATION_DATE_DESC);

    onFilterChange(clearedFilters);
  };

  const countActiveFilters = () => {
    let count = 0;
    if (filters.completionStatusFilter) count++;
    if (filters.checkInStatusFilter) count++;
    if (filters.checkInFlaggedIssueFilter) count++;
    if (filters.confirmationStatusFilter) count++;
    if (filters.origin) count++;
    if (filters.vendorName) count++;
    if (filters.senderEmail) count++;
    if (filters.hasTrackingNo !== undefined) count++;
    if (filters.hasInvoice !== undefined) count++;
    if (filters.isOverdueDelivery !== undefined) count++;
    if (filters.isInvoicePaymentDue !== undefined) count++;
    if (filters.poRelationshipType) count++;
    if (localDateTimeRange.start) {
      const defaultStart = defaultStartDateRangeDate();
      const isDefault =
        localDateTimeRange.start.getFullYear() === defaultStart.getFullYear() &&
        localDateTimeRange.start.getMonth() === defaultStart.getMonth() &&
        localDateTimeRange.start.getDate() === defaultStart.getDate() &&
        localDateTimeRange.start.getHours() === defaultStart.getHours() &&
        localDateTimeRange.start.getMinutes() === defaultStart.getMinutes();
      if (!isDefault) count++;
    }
    if (localDateTimeRange.end) count++;
    if (localDeliveryDateTimeRange.start) count++;
    if (localDeliveryDateTimeRange.end) count++;
    if (localReceiptDateTimeRange.start) count++;
    if (localReceiptDateTimeRange.end) count++;
    if (localSortOption !== CreationDateSortOptions.CREATION_DATE_DESC) count++;
    return count;
  };

  const formatDateForInput = (date?: Date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const formatDateForDateInput = (date?: Date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };

  const handleDateChange = (field: "start" | "end", value: string, rangeType: "creation" | "delivery" | "receipt") => {
    if (rangeType !== "creation") {
      const currentRange = rangeType === "delivery" ? localDeliveryDateTimeRange : localReceiptDateTimeRange;
      const setRange = rangeType === "delivery" ? setLocalDeliveryDateTimeRange : setLocalReceiptDateTimeRange;

      if (!value) {
        setRange({
          ...currentRange,
          [field]: undefined
        });
        return;
      }

      const newDate = new Date(`${value}T00:00:00.000Z`);

      if (field === "start") {
        setRange({
          ...currentRange,
          start: newDate,
          end: currentRange.end && newDate > currentRange.end ? newDate : currentRange.end
        });
      } else {
        setRange({
          ...currentRange,
          end: newDate,
          start: currentRange.start && newDate < currentRange.start ? newDate : currentRange.start
        });
      }
      return;
    }

    if (!value) {
      const newRange = {
        ...localDateTimeRange,
        [field]: undefined
      };
      setLocalDateTimeRange(newRange);
      return;
    }

    const newDate = new Date(value);
    const maxStartDate = defaultMaxStartDateRangeDate();
    const maxEndDate = new Date();

    if (field === "start") {
      // Start date should be >= maxStartDate (6 months ago) and <= end date or today
      const maxAllowedStart = localDateTimeRange.end ? new Date(localDateTimeRange.end) : maxEndDate;
      const clampedStart =
        newDate < maxStartDate ? maxStartDate : newDate > maxAllowedStart ? maxAllowedStart : newDate;

      setLocalDateTimeRange({
        start: clampedStart,
        end: localDateTimeRange.end && clampedStart > localDateTimeRange.end ? clampedStart : localDateTimeRange.end
      });
    } else {
      // End date should be >= start date and <= today
      const minAllowedEnd = localDateTimeRange.start ? new Date(localDateTimeRange.start) : maxStartDate;
      const clampedEnd = newDate < minAllowedEnd ? minAllowedEnd : newDate > maxEndDate ? maxEndDate : newDate;

      setLocalDateTimeRange({
        ...localDateTimeRange,
        end: clampedEnd
      });
    }
  };

  if (loadingDropdownOptions) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingState type="LOADING" />
      </div>
    );
  }

  if (dropdownOptionsFetchError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <p className="text-sm text-secondary-600">Failed to load options</p>
        <Button onClick={fetchDropdownOptions} variant="outline" size="sm">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <div className="flex items-center gap-1.5">
            <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Filters</h3>
            {countActiveFilters() > 0 && (
              <Badge variant="default" className="bg-secondary-900 text-white text-xs px-1.5 py-0 h-4">
                {countActiveFilters()}
              </Badge>
            )}
          </div>
          {countActiveFilters() > 0 && (
            <button
              onClick={handleClearAllFilters}
              className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
            >
              <Trash2 className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>

        <div className="space-y-2.5">
          <DropdownField
            label="Completion Status"
            value={filters.completionStatusFilter}
            onChange={(value) => handleFilterChange("completionStatusFilter", value as any)}
            options={COMPLETION_STATUS_OPTIONS}
          />

          <DropdownField
            label="Vendor"
            value={filters.vendorName}
            onChange={(value) => handleFilterChange("vendorName", value)}
            options={dropdownOptions.vendorNames || []}
          />

          <DropdownField
            label="Location"
            value={filters.origin}
            onChange={(value) => handleFilterChange("origin", value)}
            options={dropdownOptions.locations || []}
          />

          <DropdownField
            label="Sender Email"
            value={filters.senderEmail}
            onChange={(value) => handleFilterChange("senderEmail", value)}
            options={dropdownOptions.senderEmails || []}
          />

          <DropdownField
            label="Confirmation Status"
            value={filters.confirmationStatusFilter}
            onChange={(value) => handleFilterChange("confirmationStatusFilter", value as any)}
            options={CONFIRMATION_STATUS_OPTIONS}
          />

          <DropdownField
            label="Check-in Status"
            value={filters.checkInStatusFilter}
            onChange={(value) => handleFilterChange("checkInStatusFilter", value as any)}
            options={CHECK_IN_STATUS_OPTIONS}
          />

          <DropdownField
            label="Check-in Flagged Issues"
            value={filters.checkInFlaggedIssueFilter}
            onChange={(value) => handleFilterChange("checkInFlaggedIssueFilter", value as any)}
            options={CHECK_IN_FLAGGED_ISSUE_OPTIONS}
          />

          <DropdownField
            label="Has Tracking"
            value={filters.hasTrackingNo === undefined ? undefined : filters.hasTrackingNo ? "Yes" : "No"}
            onChange={(value) => handleFilterChange("hasTrackingNo", value === undefined ? undefined : value === "Yes")}
            options={["Yes", "No"]}
          />

          <DropdownField
            label="Has Invoice"
            value={filters.hasInvoice === undefined ? undefined : filters.hasInvoice ? "Yes" : "No"}
            onChange={(value) => handleFilterChange("hasInvoice", value === undefined ? undefined : value === "Yes")}
            options={["Yes", "No"]}
          />

          <DropdownField
            label="Overdue Delivery"
            value={filters.isOverdueDelivery === undefined ? undefined : filters.isOverdueDelivery ? "Yes" : "No"}
            onChange={(value) =>
              handleFilterChange("isOverdueDelivery", value === undefined ? undefined : value === "Yes")
            }
            options={["Yes", "No"]}
          />

          <DropdownField
            label="Payment Due"
            value={filters.isInvoicePaymentDue === undefined ? undefined : filters.isInvoicePaymentDue ? "Yes" : "No"}
            onChange={(value) =>
              handleFilterChange("isInvoicePaymentDue", value === undefined ? undefined : value === "Yes")
            }
            options={["Yes", "No"]}
          />

          <DropdownField
            label="PO Relationship Type"
            value={filters.poRelationshipType}
            onChange={(value) => handleFilterChange("poRelationshipType", value as any)}
            options={["Parent POs", "Child POs", "Standalone POs"]}
          />

          {/* Creation Date Range */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">
              Creation Date - Start
            </label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.start)}
              onChange={(e) => handleDateChange("start", e.target.value, "creation")}
              min={formatDateForInput(defaultMaxStartDateRangeDate())}
              max={formatDateForInput(localDateTimeRange.end || new Date())}
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">
              Creation Date - End
            </label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.end)}
              onChange={(e) => handleDateChange("end", e.target.value, "creation")}
              min={formatDateForInput(localDateTimeRange.start || defaultMaxStartDateRangeDate())}
              max={formatDateForInput(new Date())}
              className="text-sm h-9"
            />
          </div>

          {/* Delivery Date Range */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Delivery Date</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="date"
                value={formatDateForDateInput(localDeliveryDateTimeRange.start)}
                onChange={(e) => handleDateChange("start", e.target.value, "delivery")}
                min={formatDateForDateInput(defaultMaxStartDateRangeDate())}
                max={
                  localDeliveryDateTimeRange.end ? formatDateForDateInput(localDeliveryDateTimeRange.end) : undefined
                }
                className="text-sm h-9 flex-1"
                placeholder="Start"
              />
              <span className="text-sm text-secondary-600">_</span>
              <Input
                type="date"
                value={formatDateForDateInput(localDeliveryDateTimeRange.end)}
                onChange={(e) => handleDateChange("end", e.target.value, "delivery")}
                min={formatDateForDateInput(localDeliveryDateTimeRange.start || defaultMaxStartDateRangeDate())}
                max={undefined}
                className="text-sm h-9 flex-1"
                placeholder="End"
              />
            </div>
          </div>

          {/* Receipt Date Range */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Receipt Date</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="date"
                value={formatDateForDateInput(localReceiptDateTimeRange.start)}
                onChange={(e) => handleDateChange("start", e.target.value, "receipt")}
                min={formatDateForDateInput(defaultMaxStartDateRangeDate())}
                max={formatDateForDateInput(localReceiptDateTimeRange.end || new Date())}
                className="text-sm h-9 flex-1"
                placeholder="Start"
              />
              <span className="text-sm text-secondary-600">_</span>

              <Input
                type="date"
                value={formatDateForDateInput(localReceiptDateTimeRange.end)}
                onChange={(e) => handleDateChange("end", e.target.value, "receipt")}
                min={formatDateForDateInput(localReceiptDateTimeRange.start || defaultMaxStartDateRangeDate())}
                max={formatDateForDateInput(new Date())}
                className="text-sm h-9 flex-1"
                placeholder="End"
              />
            </div>
          </div>

          {/* Sort */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Sort Order</label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_DESC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Newest
              </button>
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_ASC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_ASC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Oldest
              </button>
            </div>
          </div>
        </div>

        {/* Apply Button */}
        <div className="sticky -bottom-4 pt-3 pb-1 bg-background border-t border-secondary-200 -mx-6 px-6 z-10">
          <Button
            onClick={handleApplyFilters}
            className="w-full bg-secondary-900 hover:bg-secondary-800 h-9 text-sm font-medium text-white"
          >
            Apply Changes
          </Button>
        </div>
      </div>

      {/* Column Visibility */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Show/Hide Columns</h3>
          <div className="flex items-center gap-2">
            {columnsToHide.length > 0 && (
              <button
                onClick={() => onColumnsToHideChange([])}
                className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Reset
              </button>
            )}
            <span className="text-xs text-secondary-600 font-medium">
              {allColumns.length - columnsToHide.length}/{allColumns.length}
            </span>
          </div>
        </div>
        <div className="space-y-1 max-h-44 overflow-y-auto pr-1 scrollbar-thin">
          {allColumns.map((column) => {
            const isVisible = !columnsToHide.includes(column);
            return (
              <button
                key={column}
                onClick={() => {
                  if (isVisible) {
                    onColumnsToHideChange([...columnsToHide, column]);
                  } else {
                    onColumnsToHideChange(columnsToHide.filter((c) => c !== column));
                  }
                }}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all ${
                  isVisible
                    ? "bg-secondary-50 text-secondary-900 hover:bg-secondary-100"
                    : "bg-transparent text-secondary-400 hover:bg-secondary-50"
                }`}
              >
                <span className="font-medium truncate">{column}</span>
                <Badge
                  variant={isVisible ? "default" : "outline"}
                  className={`text-xs px-1.5 py-0 h-4 flex-shrink-0 ml-2 ${isVisible ? "bg-green-500 hover:bg-green-600" : ""}`}
                >
                  {isVisible ? "Visible" : "Hidden"}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
